import { Save, RotateCcw, FileText, Camera, Shield } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';

const SystemSettingsKYC = ({ form, onSubmit, saving, resetToDefaults }) => {
  const documentTypes = [
    { value: 'national_id', label: 'National ID' },
    { value: 'passport', label: 'Passport' },
    { value: 'driver_license', label: 'Driver License' },
    { value: 'birth_certificate', label: 'Birth Certificate' },
    { value: 'utility_bill', label: 'Utility Bill' },
  ];

  return (
    <TabsContent value='kyc'>
      <Card>
        <CardHeader>
          <CardTitle>KYC Settings</CardTitle>
          <CardDescription>
            Configure Know Your Customer verification parameters and thresholds.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Shield className='h-5 w-5 mr-2' />
                  Verification Settings
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormField
                    control={form.control}
                    name='autoApprovalThreshold'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Auto-Approval Threshold (%)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={0}
                              max={100}
                              step={1}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value}% confidence required for
                              auto-approval
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Minimum confidence score for automatic KYC approval.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='documentRetentionDays'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Document Retention (days)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={30}
                              max={2555}
                              step={30}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value} days (
                              {Math.round((field.value / 365) * 10) / 10} years)
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          How long to retain user documents after verification.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  <FormField
                    control={form.control}
                    name='autoApprovalEnabled'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            Auto-Approval
                          </FormLabel>
                          <FormDescription>
                            Enable automatic KYC approval for high-confidence
                            cases.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='manualReviewRequired'
                    render={({ field }) => (
                      <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                        <div className='space-y-0.5'>
                          <FormLabel className='text-base'>
                            Manual Review
                          </FormLabel>
                          <FormDescription>
                            Require manual review for all KYC submissions.
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <Camera className='h-5 w-5 mr-2' />
                  Face Recognition Settings
                </h3>

                <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                  <FormField
                    control={form.control}
                    name='faceMatchThreshold'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Face Match Threshold (%)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={0}
                              max={100}
                              step={1}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value}% similarity required
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Minimum similarity score for face matching.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name='livenessThreshold'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Liveness Detection Threshold (%)</FormLabel>
                        <FormControl>
                          <div className='space-y-3'>
                            <Slider
                              min={0}
                              max={100}
                              step={1}
                              value={[field.value]}
                              onValueChange={value => field.onChange(value[0])}
                              className='w-full'
                            />
                            <div className='text-center text-sm text-muted-foreground'>
                              {field.value}% liveness confidence required
                            </div>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Minimum confidence for liveness detection.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Separator />

              <div className='space-y-4'>
                <h3 className='text-lg font-medium flex items-center'>
                  <FileText className='h-5 w-5 mr-2' />
                  Document Settings
                </h3>

                <FormField
                  control={form.control}
                  name='maxDocumentSize'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Maximum Document Size (MB)</FormLabel>
                      <FormControl>
                        <div className='space-y-3'>
                          <Slider
                            min={1}
                            max={50}
                            step={1}
                            value={[
                              typeof field.value === 'number' ? field.value : 1,
                            ]}
                            onValueChange={value => field.onChange(value[0])}
                            className='w-full'
                          />
                          <div className='text-center text-sm text-muted-foreground'>
                            {typeof field.value === 'number' ? field.value : 1}{' '}
                            MB maximum file size
                          </div>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Maximum allowed file size for document uploads.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='allowedDocumentTypes'
                  render={({ field }) => {
                    const selectedTypes = Array.isArray(field.value)
                      ? field.value
                      : [];

                    return (
                      <FormItem>
                        <FormLabel>Allowed Document Types</FormLabel>
                        <FormDescription>
                          Select which document types are accepted for KYC
                          verification.
                        </FormDescription>
                        <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3'>
                          {documentTypes.map(docType => (
                            <div
                              key={docType.value}
                              className={`
                              flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors
                              ${
                                selectedTypes.includes(docType.value)
                                  ? 'bg-primary/10 border-primary'
                                  : 'hover:bg-muted'
                              }
                            `}
                              onClick={() => {
                                const currentTypes = selectedTypes;
                                if (currentTypes.includes(docType.value)) {
                                  field.onChange(
                                    currentTypes.filter(
                                      type => type !== docType.value
                                    )
                                  );
                                } else {
                                  field.onChange([
                                    ...currentTypes,
                                    docType.value,
                                  ]);
                                }
                              }}
                            >
                              <div className='flex flex-col'>
                                <span className='text-sm font-medium'>
                                  {docType.label}
                                </span>
                                {selectedTypes.includes(docType.value) && (
                                  <Badge
                                    variant='secondary'
                                    className='w-fit mt-1'
                                  >
                                    Enabled
                                  </Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              <div className='flex justify-between'>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => resetToDefaults(form, 'kyc')}
                >
                  <RotateCcw className='h-4 w-4 mr-2' />
                  Reset to Defaults
                </Button>
                <Button type='submit' disabled={saving}>
                  <Save className='h-4 w-4 mr-2' />
                  {saving ? 'Saving...' : 'Save KYC Settings'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default SystemSettingsKYC;
